import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { CAMPAIGNS_METRICS_MEDIUMS } from 'modules/missionControl/constants';
import { useInfiniteQuery } from 'react-query';
import { postRaw } from 'services/api';
import { MetricName } from 'modules/campaigns/alerts/constants';
import {
  ApiRecentCampaignAlert,
  RecentCampaignAlert,
  RecentCampaignAlertsRequest,
  RecentCampaignAlertsResponse,
} from '../types/health';
import { convertDateRangeToPeriod } from '../utils/conversions';

export const MISSION_CONTROL_CAMPAIGN_ALERTS_URL = '/i/health/campaignAlerts';
export const DEFAULT_PAGE_SIZE = 5;

const dedupeById = (list: RecentCampaignAlert[]): RecentCampaignAlert[] =>
  list.reduce(
    (acc, alert) => {
      if (acc.visitedIds[alert.id]) return acc;
      return {
        visitedIds: { ...acc.visitedIds, [alert.id]: true },
        dedupedItems: [...acc.dedupedItems, alert],
      };
    },
    { visitedIds: {} as Record<number, boolean>, dedupedItems: [] as RecentCampaignAlert[] },
  ).dedupedItems;

const parseMetricName = (enumName: string): MetricName => {
  if (Object.values(MetricName).includes(enumName as MetricName)) {
    return enumName as MetricName;
  }
  throw new Error(`Invalid metric name: ${enumName}`);
};

const transformApiAlert = (apiAlert: ApiRecentCampaignAlert): RecentCampaignAlert => ({
  ...apiAlert,
  metricName: parseMetricName(apiAlert.metricName.enumName),
});

export type UseQueryRecentCampaignAlertsState = {
  recentAlerts: RecentCampaignAlert[];
  isLoading: boolean;
  hasError: boolean;
  hasMore: boolean;
  fetchMore: () => Promise<void>;
  isFetchingMore: boolean;
  refetch: () => Promise<void>;
};

export type UseQueryRecentCampaignAlertsArgs = {
  labels: number[];
  messageTypes: number[];
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
};

/**
 * Fetches recent campaign performance alerts for the current project.
 */
export const useQueryRecentCampaignAlerts = ({
  labels,
  messageTypes,
  startDate,
  endDate,
}: UseQueryRecentCampaignAlertsArgs): UseQueryRecentCampaignAlertsState => {
  const requestBody: RecentCampaignAlertsRequest = useMemo(() => {
    return {
      filterInfo: {
        labels,
        mediums: CAMPAIGNS_METRICS_MEDIUMS,
        messageTypes,
      },
      timePeriod: convertDateRangeToPeriod(startDate, endDate),
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    };
  }, [labels, messageTypes, startDate, endDate]);

  const {
    data,
    isLoading,
    isError: hasError,
    fetchNextPage,
    hasNextPage: hasMore = false,
    refetch: refetchQuery,
    isFetchingNextPage: isFetchingMore,
  } = useInfiniteQuery({
    queryKey: [MISSION_CONTROL_CAMPAIGN_ALERTS_URL, requestBody],
    queryFn: async ({ pageParam: page = 1 }): Promise<RecentCampaignAlertsResponse> => {
      if (!MISSION_CONTROL_CAMPAIGN_ALERTS_URL) {
        return { campaignAlerts: [] };
      }

      const response = await postRaw<
        RecentCampaignAlertsRequest,
        { data: { campaignAlerts: ApiRecentCampaignAlert[] } }
      >(MISSION_CONTROL_CAMPAIGN_ALERTS_URL, { ...requestBody, page });

      const apiAlerts = response?.data.campaignAlerts ?? [];
      const transformedAlerts = apiAlerts.map(transformApiAlert);

      return {
        campaignAlerts: transformedAlerts,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.campaignAlerts || !Array.isArray(lastPage.campaignAlerts)) {
        return undefined;
      }

      return lastPage.campaignAlerts.length === DEFAULT_PAGE_SIZE ? allPages.length + 1 : undefined;
    },
    retry: false,
  });

  const recentAlerts = useMemo(() => {
    if (!data?.pages || !Array.isArray(data.pages)) {
      return [];
    }

    const alerts =
      data.pages
        .filter((page) => page?.campaignAlerts && Array.isArray(page.campaignAlerts))
        .flatMap((page) => page.campaignAlerts) ?? [];
    return dedupeById(alerts);
  }, [data]);

  const fetchMore = useCallback(async () => {
    await fetchNextPage();
  }, [fetchNextPage]);

  const refetch = useCallback(async () => {
    await refetchQuery();
  }, [refetchQuery]);

  return {
    recentAlerts,
    isLoading,
    hasError,
    hasMore,
    fetchMore,
    refetch,
    isFetchingMore,
  };
};
